import { find, includes, pipe, pluck, prop, propEq, unnest } from "ramda";
import { createSelector } from "reselect";
import { Maybe } from "true-myth";

import { ILocation, IBrandGroup, ILocationApp } from "types/account";
import { IRootStateProps } from "types/reducers";

function findStoreByStoreKey(
  storeKey: string,
  brands: IBrandGroup[]
): ILocation | undefined {
  return pipe(
    pluck("stores") as (brands: IBrandGroup[]) => ILocation[][],
    unnest,
    find((store: ILocation) =>
      includes(storeKey, pluck("storeKey", store.apps))
    )
  )(brands);
}

function findLocationAppByAppName(
  appName: string,
  location: ILocation
): ILocationApp | undefined {
  return find(propEq("name", appName))(location.apps);
}

const selectAccount = ({ account }: IRootStateProps) => account;

const selectSalesForecast = createSelector(
  selectAccount,
  prop("salesForecast")
);

const selectDayParts = createSelector(selectAccount, prop("dayParts"));

const selectPermalink = createSelector(selectAccount, prop("permalink"));

const selectStoreLocation = createSelector(
  selectAccount,
  ({ locations, permalink }) => {
    const bcLink = permalink.unwrapOr("");
    return Maybe.of(findStoreByStoreKey(bcLink, locations));
  }
);

const selectPrettyURL = createSelector(
  selectAccount,
  selectStoreLocation,
  (account, location) => {
    const storePrettyUrl = location
      .andThen((loc) => Maybe.of(findLocationAppByAppName("swipely", loc)))
      .match({
        Just: (locationApp) => locationApp.storeKey,
        Nothing: () => "",
      });

    return {
      storePrettyUrl,
      locationsRequestStatus: account.requestStatuses.locationsGet,
    };
  }
);

export {
  selectSalesForecast,
  selectDayParts,
  selectPermalink,
  selectPrettyURL,
  selectStoreLocation,
};
